import { zodResolver } from '@hookform/resolvers/zod';
import { selectUserProjectTimeZone } from 'core/userContext';
import {
  MAX_CAMPAIGN_RECURRENCE_FREQUENCY,
  MAX_PRE_CREATE_RECURRING_CAMPAIGN_HOURS,
  MIN_CAMPAIGN_RECURRENCE_FREQUENCY,
  MIN_PRE_CREATE_RECURRING_CAMPAIGN_HOURS,
  MIN_RECURRING_CAMPAIGN_END_ON_DAYS,
} from 'modules/campaigns/lib/constants';
import { RecurrenceScheduleType } from 'modules/campaigns/lib/types';
import { Day, ScheduleType } from 'modules/domains/campaign';
import { momentZodType } from 'lib/zod';
import { FC, PropsWithChildren, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { z } from 'zod';
import { getIsRecurringScheduleType } from 'modules/campaigns/lib/utils';
import { useCampaignContext } from '../../context';
import { EndRecurrenceType, ScheduleFormState } from '../../types';
import {
  getIsEndDateAfterStartDate,
  getIsStartDateInTheFuture,
  getIsStartTimeInTheFuture,
  getIsTimeValid,
  getScheduleFormInitialValues,
} from './ScheduleCampaignModal.utils';

/**
 * TODO: Move this to a shared location.
 */
const RECURRING_RECURRENCE_SCHEDULE_TYPES = Object.values(RecurrenceScheduleType).filter(
  getIsRecurringScheduleType,
);

const BaseSchema = z.object({
  startDate: momentZodType,
  startTime: z.string().refine(getIsTimeValid, {
    message: 'Time must be valid. 12 and 24-hour formats are supported.',
  }),
  recurrence: z.nativeEnum(RecurrenceScheduleType),
  projectTimeZone: z.string().optional(),

  /**
   * Note: these values are actually conditionally required.
   * It's enforced via "refinements" method.
   */
  scheduleType: z.nativeEnum(ScheduleType).optional(), // this is optional because it does not apply to campaigns with STO enabled
  startingTimeZone: z.string().optional(),
  fallbackTimeZone: z.string().optional(),
});

const NonRecurringCampaignSchema = BaseSchema.extend({
  recurrence: z.enum<RecurrenceScheduleType, [RecurrenceScheduleType]>([
    RecurrenceScheduleType.DoNotRepeat,
  ]),
});

const RecurringCampaignSchema = BaseSchema.extend({
  recurrence: z.enum<RecurrenceScheduleType, [RecurrenceScheduleType, ...RecurrenceScheduleType[]]>(
    RECURRING_RECURRENCE_SCHEDULE_TYPES as [RecurrenceScheduleType, ...RecurrenceScheduleType[]],
  ),
  endType: z.nativeEnum(EndRecurrenceType),
  endDate: momentZodType.optional(),
  precreateHours: z.coerce
    .string()
    .min(1, "Can't be empty.")
    .pipe(
      z.coerce
        .number()
        .min(
          MIN_PRE_CREATE_RECURRING_CAMPAIGN_HOURS,
          `The minimum is ${MIN_PRE_CREATE_RECURRING_CAMPAIGN_HOURS} hours.`,
        )
        .max(
          MAX_PRE_CREATE_RECURRING_CAMPAIGN_HOURS,
          `The maximum is ${MAX_PRE_CREATE_RECURRING_CAMPAIGN_HOURS} hours.`,
        ),
    ),
  recurrenceFrequency: z.coerce
    .string()
    .min(1, "Can't be empty.")
    .pipe(
      z.coerce
        .number()
        .min(
          MIN_CAMPAIGN_RECURRENCE_FREQUENCY,
          `The minimum is ${MIN_CAMPAIGN_RECURRENCE_FREQUENCY} day.`,
        )
        .max(
          MAX_CAMPAIGN_RECURRENCE_FREQUENCY,
          `The maximum is ${MAX_CAMPAIGN_RECURRENCE_FREQUENCY} days.`,
        ),
    )
    .optional(),
  skipOnDays: z.array(z.nativeEnum(Day)).max(6, "Can't skip all days.").optional(),
});

/**
 * This schema is used to validate the scheduling form values.
 *
 * A few notes:
 *
 * 1. When `shouldUnregister` is set to `true`, the values for unmounted fields are not available(i.e. `undefined`).
 * @see https://react-hook-form.com/api/useform/#shouldUnregister
 * @see https://github.com/colinhacks/zod/discussions/2099#discussioncomment-5101317
 */
const schema = z
  .discriminatedUnion('recurrence', [NonRecurringCampaignSchema, RecurringCampaignSchema])
  .refine(getIsStartDateInTheFuture, {
    path: ['startDate'],
    message: 'Date must be in the future in the selected time zone.',
  })
  .refine(getIsStartTimeInTheFuture, {
    path: ['startTime'],
    message: 'Time must be in the future in the selected time zone.',
  })
  .refine(getIsEndDateAfterStartDate, {
    path: ['endDate'],
    message: `End date must be at least ${MIN_RECURRING_CAMPAIGN_END_ON_DAYS} day after the start date.`,
  });

export const ScheduleCampaignFormProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const projectTimeZone = useSelector(selectUserProjectTimeZone);
  const { campaign } = useCampaignContext();

  const defaultValues = useMemo(() => getScheduleFormInitialValues(campaign, projectTimeZone), []);

  const methods = useForm<ScheduleFormState>({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(schema),
    shouldUnregister: true,
  });

  /**
   * Validate default values on mount.
   * This is especially done because `isDirty` is not set to true until the user interacts with the form,
   * but we want to allow users to submit the form with the default values.
   * @see https://github.com/react-hook-form/react-hook-form/issues/2755
   */
  useEffect(() => {
    methods.trigger();
  }, []);

  return <FormProvider {...methods}>{children}</FormProvider>;
};
